/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { CardMetadata, breakpoints, useLocale } from '@coursera/cds-core';

import LearningProductCardColorCodedPillTags from 'bundles/product-card/components/LearningProductCardColorCodedPillTags';
import ProductCardRatings from 'bundles/product-card/components/ProductCardRatings';
import ProductCardReviews, { getProductCardReviewsAsString } from 'bundles/product-card/components/ProductCardReviews';
import StopPropagationWrapper from 'bundles/product-card/components/StopPropagationWrapper';

const styles = {
  buttons: css`
    margin-top: var(--cds-spacing-200);

    button.pathways-recommendation-coach {
      box-shadow: none;
    }

    ${breakpoints.down('sm')} {
      margin-top: var(--cds-spacing-150);

      > span {
        width: 100%;
        flex-direction: column-reverse;

        div.with-enroll-modal > button {
          width: 100%;
        }
      }
    }
  `,
  colorCodedPillTagsContainer: css`
    width: stretch;
    margin-top: calc(var(--cds-spacing-50) + var(--cds-spacing-25));
  `,
};

type Props = {
  isMetadataAboveProductRating: boolean;
  cardMetadata: string[];
  avgProductRating?: number;
  numProductRatings?: number;
  badge?: React.ReactNode;
  shouldUseColorCodedPillTags: boolean;
  statusTagNames?: string[];
  buttons?: Array<React.ReactElement>;
};

const LearningProductCardFooter = ({
  isMetadataAboveProductRating,
  cardMetadata,
  avgProductRating,
  numProductRatings,
  badge,
  shouldUseColorCodedPillTags,
  statusTagNames,
  buttons,
}: Props) => {
  const locale = useLocale();

  const renderRating = () =>
    Boolean(avgProductRating) && Boolean(numProductRatings) ? (
      <ProductCardRatings avgProductRating={avgProductRating} numProductRatings={numProductRatings} />
    ) : null;

  const renderReviews = () => {
    if (shouldUseColorCodedPillTags) {
      return null;
    }
    return Boolean(avgProductRating) && Boolean(numProductRatings) ? (
      <ProductCardReviews avgProductRating={avgProductRating} numProductRatings={numProductRatings} />
    ) : null;
  };

  const renderColorCodedPillTags = () => {
    return <LearningProductCardColorCodedPillTags pillTags={statusTagNames?.map((tag) => ({ text: tag })) ?? []} />;
  };

  const buttonsWithStopPropagation = buttons?.map((button) => {
    return React.isValidElement(button) ? <StopPropagationWrapper>{button}</StopPropagationWrapper> : button;
  });

  const renderButtons = () =>
    buttonsWithStopPropagation && buttonsWithStopPropagation.length > 0 ? (
      <div css={styles.buttons}>{buttonsWithStopPropagation}</div>
    ) : null;

  const renderCardMetadata = () => {
    if (shouldUseColorCodedPillTags) {
      const reviewText = getProductCardReviewsAsString(locale, avgProductRating, numProductRatings);
      const metadataToUse = reviewText ? [reviewText, ...cardMetadata] : cardMetadata;
      return <CardMetadata metadata={metadataToUse} />;
    }
    return cardMetadata.length > 0 ? <CardMetadata metadata={cardMetadata} /> : null;
  };

  if (isMetadataAboveProductRating) {
    if (shouldUseColorCodedPillTags) {
      return renderCardMetadata();
    }
    return (
      <>
        {renderCardMetadata()}
        {renderRating()}
      </>
    );
  }

  if (shouldUseColorCodedPillTags) {
    return (
      <>
        {badge}
        {renderCardMetadata()}
        <div css={styles.colorCodedPillTagsContainer}>{renderColorCodedPillTags()}</div>
        {renderButtons()}
      </>
    );
  }

  return (
    <>
      {badge}
      {renderReviews()}
      {renderCardMetadata()}
      {renderButtons()}
    </>
  );
};

export default LearningProductCardFooter;
